/**
 * ColorGeneratorModal — iframe-based color stack generator
 */
import { useEffect, useRef } from 'react';
import { useBuilderStore } from '@/state/store';
import { b64ToUtf8 } from '@/utils/base64';
import { Modal } from './Modal';

// The color generator base64 blob will be imported at build time
// For now, use a placeholder that will be replaced during integration
declare const __COLOR_GEN_B64__: string;

export function ColorGeneratorModal() {
  const openModal = useBuilderStore((s) => s.openModal);
  const setOpenModal = useBuilderStore((s) => s.setOpenModal);
  const updateSettings = useBuilderStore((s) => s.updateSettings);
  const settings = useBuilderStore((s) => s.settings);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const isOpen = openModal === 'colorGen';
  const onClose = () => setOpenModal(null);

  useEffect(() => {
    if (!isOpen) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    if (!(iframe as any).__loaded) {
      try {
        iframe.srcdoc = b64ToUtf8(typeof __COLOR_GEN_B64__ !== 'undefined' ? __COLOR_GEN_B64__ : '');
        (iframe as any).__loaded = true;
      } catch (e) {
        // Color gen not available yet
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const data = e?.data;
      if (!data || data.type !== "sb-color-stack") return;
      updateSettings({ ...settings, colorStack: data.payload, useColorStack: true });
      onClose();
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [settings, updateSettings, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Color Stack Generator"
      footer={<button className="btn btn-primary" onClick={onClose}>Schliessen</button>}
    >
      <div style={{ border: "1px solid var(--builder-border)", borderRadius: 12, overflow: "hidden", height: "70vh" }}>
        <iframe
          ref={iframeRef}
          title="Color Stack Generator"
          style={{ width: "100%", height: "100%", border: 0, background: "white" }}
        />
      </div>
      <p style={{ marginTop: 12, fontSize: 12, color: "var(--builder-text-muted)" }}>
        Workflow: Palette generieren {"→"} {"„In Builder übernehmen“"} {"→"} dann ggf. Overrides aktivieren.
      </p>
    </Modal>
  );
}
